import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Wifi, Globe, MapPin, ChevronRight } from 'lucide-react';

const SLIDES = [
  {
    image: 'https://static.prod-images.emergentagent.com/jobs/cf702e61-1c5c-4082-ad24-9b20f1215a59/images/f9b60ff6a6c6d3ee59b4f344cf57215a17e0335a6e11cf4d57fe289ff1bab65b.png',
    city: 'Tokyo',
    country: 'Japan',
    taglineKey: 'hero.tagline1'
  },
  {
    image: 'https://static.prod-images.emergentagent.com/jobs/cf702e61-1c5c-4082-ad24-9b20f1215a59/images/22f39f345bf856f612a34b96cff1a7918eb138364ad30d0c1c86f409a3b7d1d3.png',
    city: 'Tropical Beach',
    country: 'Paradise',
    taglineKey: 'hero.tagline2'
  },
  {
    image: 'https://static.prod-images.emergentagent.com/jobs/cf702e61-1c5c-4082-ad24-9b20f1215a59/images/aa7aaa1a65bd2c640d96e6102427d745911807bb4235535be23623dfca7d89bd.png',
    city: 'Rome',
    country: 'Italy',
    taglineKey: 'hero.tagline3'
  },
  {
    image: 'https://static.prod-images.emergentagent.com/jobs/cf702e61-1c5c-4082-ad24-9b20f1215a59/images/5952ccd09e711d4e56ae2de2ad343e8379b25534dae61635cde232eb724d8a78.png',
    city: 'Airport',
    country: 'Worldwide',
    taglineKey: 'hero.tagline4'
  },
  {
    image: 'https://images.unsplash.com/photo-1613488328514-e424950c0b0d?w=1920&q=80',
    city: 'Tokyo',
    country: 'Night Life',
    taglineKey: 'hero.tagline5'
  },
  {
    image: 'https://images.unsplash.com/photo-1735320862043-8d62ab24ce1b?w=1920&q=80',
    city: 'Dubai',
    country: 'UAE',
    taglineKey: 'hero.tagline6'
  },
  {
    image: 'https://images.unsplash.com/photo-1769517250449-a8a37bc452e5?w=1920&q=80',
    city: 'New York',
    country: 'USA',
    taglineKey: 'hero.tagline7'
  }
];

export default function TravelHero({ totalProducts, onSearch, onNavigate }) {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchValue, setSearchValue] = useState('');

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % SLIDES.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      onSearch(searchValue.trim());
    }
  };

  const slide = SLIDES[currentSlide];

  return (
    <section className="relative h-[85vh] min-h-[600px] overflow-hidden" data-testid="travel-hero">
      {/* Background Images */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.08 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="absolute inset-0"
        >
          <img
            src={slide.image}
            alt={slide.city}
            className="w-full h-full object-cover"
          />
          {/* Overlay - lighter to let images shine */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] via-[#0f1419]/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f1419]/60 via-transparent to-[#0f1419]/20" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-end pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Current destination label */}
        <motion.div
          key={`label-${currentSlide}`}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex items-center space-x-2 mb-4"
        >
          <MapPin className="w-4 h-4 text-teal-400" />
          <span className="text-teal-400 text-sm font-medium tracking-wide uppercase">{slide.city}, {slide.country}</span>
        </motion.div>

        {/* Main heading */}
        <motion.h1
          key={`h1-${currentSlide}`}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-outfit mb-3 max-w-2xl leading-tight"
          data-testid="hero-title"
        >
          <span className="text-teal-400">{t('hero.title')}</span>
        </motion.h1>

        <motion.p
          key={`p-${currentSlide}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-lg text-slate-300 mb-8 max-w-xl"
        >
          {t(slide.taglineKey)}
        </motion.p>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex max-w-lg mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={t('hero.searchPlaceholder', 'Search destination...')}
              className="w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-l-full pl-12 pr-4 py-4 text-white placeholder-slate-400 focus:outline-none focus:border-teal-500/50 focus:bg-white/15 transition-all"
              data-testid="hero-search-input"
            />
          </div>
          <button
            type="submit"
            className="bg-teal-500 hover:bg-teal-400 text-black font-bold px-8 rounded-r-full transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] flex items-center space-x-2"
            data-testid="hero-search-button"
          >
            <span className="hidden sm:inline">{t('common.search')}</span>
            <ChevronRight className="w-5 h-5" />
          </button>
        </form>

        {/* Stats Row */}
        <div className="flex flex-wrap items-center gap-6 sm:gap-10">
          <div className="flex items-center space-x-2" data-testid="hero-stat-destinations">
            <Globe className="w-5 h-5 text-teal-400" />
            <span className="text-white font-bold text-lg">{totalProducts || 213}+</span>
            <span className="text-slate-400 text-sm">{t('hero.destinations', 'Destinations')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Wifi className="w-5 h-5 text-teal-400" />
            <span className="text-white font-bold text-lg">4G/5G</span>
            <span className="text-slate-400 text-sm">{t('hero.coverage', 'Coverage')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-teal-400 text-lg font-bold">QR</span>
            <span className="text-slate-400 text-sm">{t('hero.instantDelivery', 'Instant Delivery')}</span>
          </div>
        </div>

        {/* Slide indicators */}
        <div className="flex items-center space-x-2 mt-8">
          {SLIDES.map((s, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`transition-all duration-300 rounded-full ${
                i === currentSlide
                  ? 'w-8 h-2 bg-teal-400'
                  : 'w-2 h-2 bg-white/30 hover:bg-white/50'
              }`}
              data-testid={`slide-indicator-${i}`}
            />
          ))}
          <span className="text-slate-500 text-xs ml-3">{slide.city}</span>
        </div>
      </div>
    </section>
  );
}
